import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, TrendingDown, FileBarChart, ListChecks, Target, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import MoneyInput from '../MoneyInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatMoney = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);
  } catch { return `$${n}`; }
};

/* FinancialSummaryTab — top-of-dialog roll-up tiles (Total CapEx, OpEx,
   FF&E, Contingency, Pre-Opening, etc.) plus a grand-total ribbon. Each
   tile compares actual spend vs. the admin-configured target so the GM
   can spot variances at a glance. Admins can edit targets inline.
   Pulls from /api/admin/financial/summary which aggregates BOTH uploaded
   budget docs (with `amount` set) and live builder line items. */
export default function FinancialSummaryTab({ adminToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Local override map so editing a target shows green check immediately.
  const [editingCat, setEditingCat] = useState(null);
  const [editValue, setEditValue] = useState('');
  // Role detection — only admin/engineer can edit targets. Editor/viewer
  // see the Target row read-only.
  const userRole = typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '') : '';
  const canEditTargets = ['admin', 'engineer'].includes(userRole);

  const load = useCallback(() => {
    if (!adminToken) return;
    setLoading(true);
    axios.get(`${API}/admin/financial/summary`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const saveTarget = async (category) => {
    const amount = editValue.trim() === '' ? null : parseFloat(editValue);
    if (editValue.trim() !== '' && Number.isNaN(amount)) {
      toast.error('Enter a valid number');
      return;
    }
    try {
      // Read full targets, mutate, send back so we don't drop sibling categories.
      const { data: cur } = await axios.get(`${API}/admin/financial/category-targets`, { headers: { Authorization: `Bearer ${adminToken}` } });
      const next = { ...(cur.targets || {}) };
      if (amount === null) {
        delete next[category];
      } else {
        next[category] = amount;
      }
      await axios.put(`${API}/admin/financial/category-targets`, { targets: next }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Target saved');
      setEditingCat(null);
      setEditValue('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  if (!adminToken) {
    return <div className="p-8 text-center text-sm" style={{ color: '#7A8C8C' }}>Sign in as admin to see the financial summary.</div>;
  }
  if (loading) {
    return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" style={{ color: '#003DA5' }} /></div>;
  }
  if (!data) {
    return <div className="p-8 text-center text-sm" style={{ color: '#DC2626' }}>Could not load summary.</div>;
  }

  return (
    <div data-testid="financial-summary-tab" className="space-y-4">
      {/* Headline ribbon */}
      <div className="rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-3" style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)' }}>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70" style={{ fontFamily: 'Manrope' }}>
            Grand total tracked
          </p>
          <p data-testid="financial-grand-total" className="text-3xl font-black text-white" style={{ fontFamily: 'Manrope' }}>
            {formatMoney(data.grand_total)}
          </p>
          {data.grand_target != null && (
            <p data-testid="financial-grand-target" className="text-[10px] mt-0.5 font-bold text-white/70" style={{ fontFamily: 'Manrope' }}>
              of {formatMoney(data.grand_target)} target ({((data.grand_total / data.grand_target) * 100).toFixed(0)}%)
            </p>
          )}
        </div>
        <div className="flex items-center gap-4 text-white">
          <Stat icon={FileBarChart} label="Documents" value={data.doc_count} />
          <Stat icon={ListChecks} label="Builder rows" value={data.row_count} />
          <Stat icon={TrendingUp} label="Live builder" value={formatMoney(data.builder_total)} dim />
        </div>
      </div>

      {/* Per-category tiles */}
      {data.categories.length === 0 ? (
        <div className="text-center py-12 text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
          No budgets tracked yet. Upload a budget doc or open the <strong>Builder</strong> tab to create line items.
        </div>
      ) : (
        <div data-testid="financial-categories" className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {data.categories.map((c) => {
            const isEditing = editingCat === c.category;
            // Linearise the over/under/at branching — avoids nested ternaries
            // and keeps the deltaColor lookup a simple map read.
            let overUnder = null;
            if (c.delta != null) {
              if (c.delta > 0) overUnder = 'over';
              else if (c.delta < 0) overUnder = 'under';
              else overUnder = 'at';
            }
            const DELTA_COLOR = { over: '#DC2626', under: '#15803D', at: '#7A8C8C' };
            const deltaColor = DELTA_COLOR[overUnder] || '#7A8C8C';
            return (
              <div
                key={c.category}
                data-testid={`financial-cat-${c.category}`}
                className="rounded-xl p-4 border"
                style={{ borderColor: '#E2DBC8', backgroundColor: 'white' }}
              >
                <p className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                  {c.label}
                </p>
                <p className="text-2xl font-bold mt-1" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                  {formatMoney(c.amount)}
                </p>

                {/* Target row */}
                <div className="mt-2 pt-2 border-t" style={{ borderColor: '#F1ECDD' }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                      <Target className="w-2.5 h-2.5" /> Target
                    </div>
                    {!isEditing && c.target != null && (
                      <span data-testid={`financial-cat-target-${c.category}`} className="text-[11px] font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                        {formatMoney(c.target)}
                      </span>
                    )}
                    {!isEditing && c.target == null && canEditTargets && (
                      <button
                        data-testid={`financial-cat-set-target-${c.category}`}
                        onClick={() => { setEditingCat(c.category); setEditValue(''); }}
                        className="text-[10px] font-bold inline-flex items-center gap-1 hover:underline"
                        style={{ color: '#003DA5', fontFamily: 'Manrope' }}
                      >
                        <Pencil className="w-2.5 h-2.5" /> Set target
                      </button>
                    )}
                    {!isEditing && c.target != null && canEditTargets && (
                      <button
                        data-testid={`financial-cat-edit-target-${c.category}`}
                        onClick={() => { setEditingCat(c.category); setEditValue(String(c.target)); }}
                        className="p-1 rounded hover:bg-slate-100"
                        title="Edit target"
                      >
                        <Pencil className="w-2.5 h-2.5" style={{ color: '#7A8C8C' }} />
                      </button>
                    )}
                  </div>

                  {isEditing && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <MoneyInput
                        data-testid={`financial-cat-target-input-${c.category}`}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="amount (blank to clear)"
                        className="flex-1 px-2 py-1 rounded text-xs border outline-none focus:border-blue-400"
                        style={{ borderColor: '#D6D0C0' }}
                        autoFocus
                      />
                      <button
                        data-testid={`financial-cat-save-target-${c.category}`}
                        onClick={() => saveTarget(c.category)}
                        className="p-1 rounded text-white"
                        style={{ backgroundColor: '#15803D' }}
                        title="Save"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => { setEditingCat(null); setEditValue(''); }}
                        className="p-1 rounded text-white"
                        style={{ backgroundColor: '#7A8C8C' }}
                        title="Cancel"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {!isEditing && c.delta != null && (
                    <div className="flex items-center gap-1 mt-1">
                      {overUnder === 'over' ? <TrendingUp className="w-2.5 h-2.5" style={{ color: deltaColor }} /> : <TrendingDown className="w-2.5 h-2.5" style={{ color: deltaColor }} />}
                      <span data-testid={`financial-cat-delta-${c.category}`} className="text-[10px] font-bold" style={{ color: deltaColor, fontFamily: 'Manrope' }}>
                        {c.delta > 0 ? '+' : ''}{formatMoney(Math.abs(c.delta))} ({c.pct_achieved.toFixed(0)}% of target)
                      </span>
                    </div>
                  )}
                </div>

                <p className="text-[10px] mt-2" style={{ color: '#7A8C8C' }}>
                  {c.doc_count} doc{c.doc_count === 1 ? '' : 's'} · {c.row_count} row{c.row_count === 1 ? '' : 's'}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, dim }) {
  return (
    <div className={`text-right ${dim ? 'opacity-80' : ''}`}>
      <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-white/70 inline-flex items-center gap-1" style={{ fontFamily: 'Manrope' }}>
        <Icon className="w-2.5 h-2.5" /> {label}
      </p>
      <p className="text-base font-bold text-white" style={{ fontFamily: 'Manrope' }}>{value}</p>
    </div>
  );
}
